import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { apiClient } from '../api/client';
import {
  ErrorMessage,
  Card,
  Badge,
  PageHeader,
  NoOrganizationMessage,
  Table,
  DataTable,
  Button,
  Select,
  Modal,
  formatDate,
  formatEuro,
} from '../components';

interface FiscalPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed' | 'locked';
}

interface OpeningEntryLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
}

interface OpeningEntryPreview {
  date: string;
  description: string;
  lines: OpeningEntryLine[];
  totalDebits: number;
  totalCredits: number;
}

interface OpeningEntry {
  id: string;
  date: string;
  description: string;
  voucherNumber: number;
  status: string;
  entryType: string;
  generatedFromPeriodId: string;
  isReadOnly: boolean;
  lines?: OpeningEntryLine[];
}

interface OpeningEntryDetail extends OpeningEntry {
  lines: OpeningEntryLine[];
}

const STATUS_COLORS: Record<string, string> = {
  approved: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  draft: 'bg-gray-100 text-gray-800',
};

const PERIOD_STATUS_COLORS: Record<string, string> = {
  open: 'bg-green-100 text-green-800',
  closed: 'bg-yellow-100 text-yellow-800',
  locked: 'bg-red-100 text-red-800',
};

export default function OpeningEntriesPage() {
  const { t } = useTranslation();
  const { activeOrg } = useAuthStore();
  const [periods, setPeriods] = useState<FiscalPeriod[]>([]);
  const [openingEntries, setOpeningEntries] = useState<OpeningEntry[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [preview, setPreview] = useState<OpeningEntryPreview | null>(null);
  const [detailEntry, setDetailEntry] = useState<OpeningEntryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [previousPeriodId, setPreviousPeriodId] = useState<string>('');

  const fetchPeriods = useCallback(async () => {
    if (!activeOrg) return;

    try {
      const data = await apiClient.get<{ fiscalPeriods: FiscalPeriod[] }>(`/fiscal-periods?organization_id=${activeOrg.id}`);
      setPeriods(data.fiscalPeriods || []);
    } catch (err) {
      console.error('Failed to fetch periods:', err);
    }
  }, [activeOrg]);

  const fetchOpeningEntries = useCallback(async (periodId: string) => {
    if (!activeOrg || !periodId) return;

    setLoading(true);
    setError('');

    try {
      const data = await apiClient.get<{ opening_entries: OpeningEntry[] }>(
        `/fiscal-periods/${periodId}/opening-entries?organization_id=${activeOrg.id}`
      );
      setOpeningEntries(data.opening_entries || []);
    } catch (err) {
      setError('Tilinavauksien haku epäonnistui');
      console.error('Failed to fetch opening entries:', err);
    } finally {
      setLoading(false);
    }
  }, [activeOrg]);

  const previewOpeningEntries = async (periodId: string, prevPeriodId: string) => {
    if (!activeOrg) return;

    setPreviewLoading(true);
    setError('');

    try {
      const data = await apiClient.post<{ preview: OpeningEntryPreview }>(
        `/fiscal-periods/${periodId}/preview-opening-entries?organization_id=${activeOrg.id}`,
        { previous_period_id: prevPeriodId }
      );
      setPreview(data.preview);
      setPreviousPeriodId(prevPeriodId);
      setShowPreviewModal(true);
    } catch (err) {
      setError('Esikatselu epäonnistui: ' + (err instanceof Error ? err.message : 'Tuntematon virhe'));
      console.error('Failed to preview:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const generateOpeningEntries = async () => {
    if (!activeOrg || !selectedPeriod || !previousPeriodId) return;

    setGenerating(true);
    setError('');

    try {
      await apiClient.post(
        `/fiscal-periods/${selectedPeriod}/generate-opening-entries?organization_id=${activeOrg.id}`,
        { previous_period_id: previousPeriodId }
      );
      alert('Tilinavaukset luotu onnistuneesti!');
      setShowPreviewModal(false);
      fetchOpeningEntries(selectedPeriod);
    } catch (err) {
      setError('Luonti epäonnistui: ' + (err instanceof Error ? err.message : 'Tuntematon virhe'));
      console.error('Failed to generate:', err);
    } finally {
      setGenerating(false);
    }
  };

  const viewEntryDetail = (entry: OpeningEntry) => {
    // Find the full entry with lines
    const fullEntry = openingEntries.find(e => e.id === entry.id);
    if (fullEntry && fullEntry.lines) {
      setDetailEntry(fullEntry as OpeningEntryDetail);
      setShowDetailModal(true);
    }
  };

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  useEffect(() => {
    if (selectedPeriod) {
      fetchOpeningEntries(selectedPeriod);
    }
  }, [selectedPeriod, fetchOpeningEntries]);

  if (!activeOrg) {
    return <NoOrganizationMessage />;
  }

  const selectedPeriodData = periods.find(p => p.id === selectedPeriod);
  const previousPeriods = periods.filter(p => p.endDate < (selectedPeriodData?.startDate || ''));

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Tilinavaukset" />

      {/* Error */}
      {error && <ErrorMessage message={error} />}

      {/* Period Selection */}
      <Card title="Valitse tilikausi">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Tilikausi"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            options={[
              { value: '', label: t('openingEntries.selectPeriod') },
              ...periods.map(p => ({
                value: p.id,
                label: `${p.name} (${formatDate(p.startDate)} - ${formatDate(p.endDate)})`,
              })),
            ]}
          />
          {selectedPeriodData && (
            <div className="flex items-end">
              <Badge className={PERIOD_STATUS_COLORS[selectedPeriodData.status]}>
                {selectedPeriodData.status === 'open' ? 'Avoin' :
                 selectedPeriodData.status === 'closed' ? 'Suljettu' : 'Lukittu'}
              </Badge>
            </div>
          )}
        </div>
      </Card>

      {/* Generate Section */}
      {selectedPeriod && (
        <Card title="Luo tilinavaukset">
          <p className="text-sm text-gray-600 mb-4">
            Valitse edellinen tilikausi, josta sulkusaldoista luodaan tilinavaukset.
          </p>
          <div className="flex gap-4">
            <Select
              label="Edellinen tilikausi"
              value={previousPeriodId}
              onChange={(e) => setPreviousPeriodId(e.target.value)}
              options={[
                { value: '', label: t('openingEntries.selectPreviousPeriod') },
                ...previousPeriods.map(p => ({
                  value: p.id,
                  label: `${p.name} (${formatDate(p.startDate)} - ${formatDate(p.endDate)})`,
                })),
              ]}
            />
            <div className="flex items-end">
              <Button
                variant="primary"
                onClick={() => previewOpeningEntries(selectedPeriod, previousPeriodId)}
                disabled={!previousPeriodId || previewLoading}
              >
                {previewLoading ? '⏳ ' + t('common.loading') : '🔍 ' + t('common.preview')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Opening Entries List */}
      <Card title={`Tilinavaukset (${openingEntries.length})`}>
        <DataTable>
          <Table
            columns={[
              {
                key: 'voucherNumber',
                header: 'Tosite',
                width: '100px',
                render: (entry: OpeningEntry) => `#${entry.voucherNumber}`,
              },
              {
                key: 'date',
                header: 'Päivämäärä',
                width: '120px',
                render: (entry: OpeningEntry) => formatDate(entry.date),
              },
              {
                key: 'description',
                header: 'Kuvaus',
                render: (entry: OpeningEntry) => entry.description,
              },
              {
                key: 'status',
                header: 'Tila',
                width: '120px',
                render: (entry: OpeningEntry) => (
                  <Badge className={STATUS_COLORS[entry.status] || 'bg-gray-100'}>
                    {entry.status === 'approved' ? 'Hyväksytty' :
                     entry.status === 'pending' ? 'Odottaa' : entry.status}
                  </Badge>
                ),
              },
              {
                key: 'readOnly',
                header: 'Tila',
                width: '100px',
                render: (entry: OpeningEntry) => (
                  entry.isReadOnly ? (
                    <span className="text-xs text-gray-500">🔒 Lukittu</span>
                  ) : (
                    <span className="text-xs text-green-600">✏️ Muokattavissa</span>
                  )
                ),
              },
              {
                key: 'actions',
                header: '',
                width: '100px',
                align: 'right',
                render: (entry: OpeningEntry) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => viewEntryDetail(entry)}
                  >
                    Näytä
                  </Button>
                ),
              },
            ]}
            data={openingEntries}
            keyExtractor={(entry) => entry.id}
            loading={loading}
            emptyMessage={selectedPeriod ? "Ei tilinavauksia tälle kaudelle" : "Valitse tilikausi"}
          />
        </DataTable>
      </Card>

      {/* Preview Modal */}
      {showPreviewModal && preview && (
        <Modal
          title="Tilinavausten esikatselu"
          onClose={() => setShowPreviewModal(false)}
          size="large"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Päivämäärä:</span>{' '}
                <span className="font-medium">{formatDate(preview.date)}</span>
              </div>
              <div>
                <span className="text-gray-500">Kuvaus:</span>{' '}
                <span className="font-medium">{preview.description}</span>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Tili</th>
                    <th className="px-4 py-2 text-right">Debet</th>
                    <th className="px-4 py-2 text-right">Kredit</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((line, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-4 py-2">
                        <div className="font-medium">{line.accountCode}</div>
                        <div className="text-gray-500 text-xs">{line.accountName}</div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {line.debit > 0 ? formatEuro(line.debit) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {line.credit > 0 ? formatEuro(line.credit) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 font-medium">
                  <tr>
                    <td className="px-4 py-2">Yhteensä</td>
                    <td className="px-4 py-2 text-right">{formatEuro(preview.totalDebits)}</td>
                    <td className="px-4 py-2 text-right">{formatEuro(preview.totalCredits)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {Math.abs(preview.totalDebits - preview.totalCredits) > 0.01 && (
              <div className="bg-red-50 text-red-700 p-3 rounded text-sm">
                ⚠️ Varoitus: Debet- ja kreditsummat eivät täsmää!
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={generateOpeningEntries}
                disabled={generating || Math.abs(preview.totalDebits - preview.totalCredits) > 0.01}
              >
                {generating ? '⏳ ' + t('common.creating') : '✓ ' + t('openingEntries.createOpeningEntries')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {showDetailModal && detailEntry && (
        <Modal
          title={`Tilinavaus #${detailEntry.voucherNumber}`}
          onClose={() => setShowDetailModal(false)}
          size="large"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Päivämäärä:</span>{' '}
                <span className="font-medium">{formatDate(detailEntry.date)}</span>
              </div>
              <div>
                <span className="text-gray-500">Tila:</span>{' '}
                <Badge className={STATUS_COLORS[detailEntry.status] || 'bg-gray-100'}>
                  {detailEntry.status}
                </Badge>
              </div>
            </div>

            {detailEntry.lines && detailEntry.lines.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Tili</th>
                      <th className="px-4 py-2 text-right">Debet</th>
                      <th className="px-4 py-2 text-right">Kredit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailEntry.lines.map((line, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-2">
                          <div className="font-medium">{line.accountCode}</div>
                          <div className="text-gray-500 text-xs">{line.accountName}</div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {line.debit > 0 ? formatEuro(line.debit) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {line.credit > 0 ? formatEuro(line.credit) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {detailEntry.isReadOnly && (
              <div className="bg-yellow-50 text-yellow-700 p-3 rounded text-sm">
                🔒 Tämä on järjestelmän luoma tilinavaus eikä ole muokattavissa.
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
